import { provide, computed, defineComponent, useContext, useCssModule, onMounted, ref, onBeforeUnmount, watch, } from '@nuxtjs/composition-api';
import { ARTICLE_BOXES, BANNERS_BY_INDEX_DESKTOP, BANNERS_BY_INDEX_MOBILE, NATIVE_ROLL_INDEX_DESKTOP, NATIVE_ROLL_INDEX_MOBILE, OBSERVABLE_TYPES, AT_TOP_ADMARK_RUBRICS, BANKRUPTCY_DISCLAIMER, } from './';
import { ADFOX_CODES_DESKTOP, ADFOX_CODES_MOBILE } from '~/components/Adfox';
import { addQueryParamForUrl, addUtmForLinksInString, createFoldedArticleBody, shouldDisableArticleAd } from '~/utils';
import { useBookmarkStore } from '~/composables';
export default defineComponent({
    name: 'ArticleBoxes',
    props: {
        apiField: {
            default: 'body',
            type: String,
            validator: (value) => {
                return ['body', 'body_after'].includes(value);
            },
        },
        article: {
            default: () => ({}),
            type: Object,
        },
        inTooltip: {
            default: false,
            type: Boolean,
        },
        isFolded: {
            default: false,
            type: Boolean,
        },
        paywall: {
            default: null,
            type: Object,
        },
        readingIndex: {
            default: -1,
            type: Number,
        },
        schemaDisabled: {
            default: false,
            type: Boolean,
        },
        shouldDisableAd: {
            default: false,
            type: Boolean,
        },
        disclaimerSize: {
            default: 0,
            type: Number,
        },
    },
    emits: ['updateReading'],
    setup(props, { emit }) {
        provide('article', props.article);
        const bookmarkStore = useBookmarkStore();
        const css = useCssModule();
        const context = useContext();
        const viewport = context.$viewport;
        const intersectionObserver = ref();
        const boxesRef = ref([]);
        const boxesReaded = ref(-1);
        const readingClosed = ref(false);
        function isStopReadingVisible(index) {
            const bookmark = bookmarkStore.data[props.article.id];
            if (readingClosed.value || bookmark === -1) {
                return false;
            }
            return boxesReaded.value === index;
        }
        const articleBody = computed(() => {
            const articleBody = props.article[props.apiField];
            if (!articleBody || !Array.isArray(articleBody) || !articleBody.length) {
                return [];
            }
            // Format body
            const body = articleBody?.reduce((acc, box) => {
                const { data = {}, type } = box || {};
                const isArticleForUtm = props.article.url_alias?.startsWith('spetsproekt') || props.article.url_alias?.startsWith('brandvoice');
                // Trim text
                if (data.text) {
                    data.text = data.text?.trim();
                }
                // Ingnore boxes with empty text
                if (data?.text === '' && type !== 'quote') {
                    return acc;
                }
                if (isArticleForUtm && data.text) {
                    data.text = addUtmForLinksInString(data.text, { name: 'sp' });
                }
                if (type === 'listOfValues' && data.items?.length) {
                    if (isArticleForUtm) {
                        data.items = data.items.map((item) => {
                            if (typeof item === 'string') {
                                return item;
                            }
                            else {
                                const newValue = item.value.startsWith('http')
                                    ? `${item.value + addQueryParamForUrl(item.value, { name: 'sp' })}`
                                    : item.value;
                                return { key: item.key, value: newValue };
                            }
                        });
                    }
                    if (data.id === 'admark' &&
                        props.article.sections?.find((section) => AT_TOP_ADMARK_RUBRICS.includes(section?.url))) {
                        box.isAdvMarker = true;
                        acc.unshift(box);
                        return acc;
                    }
                }
                acc.push(box);
                return acc;
            }, []);
            if (props.isFolded || props.paywall) {
                return createFoldedArticleBody(body).map((box, i) => {
                    box.index = i;
                    return box;
                });
            }
            if (props.shouldDisableAd || shouldDisableArticleAd(props.article)) {
                return body.map((box, i) => {
                    box.index = i;
                    return box;
                });
            }
            // Prepare banners for injecting in body
            let paragraphIndex = 0;
            let adfoxCodes = ADFOX_CODES_DESKTOP;
            let bannersByIndex = BANNERS_BY_INDEX_DESKTOP;
            let nativeRollIndex = NATIVE_ROLL_INDEX_DESKTOP;
            if (viewport.isLessThan('tablet')) {
                adfoxCodes = ADFOX_CODES_MOBILE;
                bannersByIndex = BANNERS_BY_INDEX_MOBILE;
                nativeRollIndex = NATIVE_ROLL_INDEX_MOBILE;
            }
            const bannersKeys = Object.keys(bannersByIndex);
            const firstIndex = parseInt(bannersKeys[0], 10);
            if (body.length <= firstIndex + 1) {
                return body;
            }
            const lastIndex = parseInt(bannersKeys[bannersKeys.length - 1], 10);
            const bannersDistance = lastIndex - parseInt(bannersKeys[bannersKeys.length - 2], 10);
            let repeatingIndex = lastIndex + bannersDistance;
            // Push banners in body
            return body
                .reduce((acc, box) => {
                acc.push(box);
                if (box.type !== 'paragraph') {
                    return acc;
                }
                if (paragraphIndex === nativeRollIndex) {
                    // Native Roll
                    acc.push({
                        data: {
                            bannerName: 'native-roll',
                            config: {},
                        },
                        type: 'native-roll',
                    });
                }
                else if (paragraphIndex === repeatingIndex) {
                    // Repeating banners after primary banners
                    acc.push({
                        data: {
                            bannerName: bannersByIndex[lastIndex],
                            config: adfoxCodes[bannersByIndex[lastIndex]],
                        },
                        type: 'ad',
                    });
                    repeatingIndex += bannersDistance;
                }
                else {
                    // Primary banners
                    Object.entries(bannersByIndex).forEach(([key, value]) => {
                        if (paragraphIndex === parseInt(key, 10)) {
                            acc.push({
                                data: {
                                    bannerName: value,
                                    config: adfoxCodes[value],
                                },
                                type: 'ad',
                            });
                        }
                    });
                }
                paragraphIndex++;
                return acc;
            }, [])
                .map((box, i) => {
                box.index = i;
                return box;
            });
        });
        const lastTextIndex = computed(() => {
            let i = articleBody.value.length;
            while (i--) {
                const box = articleBody.value[i];
                if (box.type === 'paragraph') {
                    const text = box.data?.text?.replace(/&\w+;/g, '');
                    if (text.trim().length) {
                        return i;
                    }
                }
            }
            return 0;
        });
        const shouldRenderAdText = false;
        function observe() {
            intersectionObserver.value?.disconnect();
            intersectionObserver.value = undefined;
            intersectionObserver.value = new IntersectionObserver(([entry], observer) => {
                const { boundingClientRect: { y }, intersectionRatio, target, } = entry || {};
                if (entry && y < 0 && intersectionRatio < 0.5) {
                    const { dataset } = target;
                    const index = Number(dataset.index);
                    if (index > props.readingIndex) {
                        emit('updateReading', index);
                    }
                    // If previous boxes were not marked as readed, then mark them
                    for (let i = 0; i < index; i++) {
                        if (OBSERVABLE_TYPES.includes(articleBody.value[i].type || '')) {
                            observer?.unobserve(boxesRef.value[i].$el);
                        }
                    }
                    observer?.unobserve(target);
                }
            }, {
                threshold: Array.from({ length: 11 }, (_, key) => key * 0.1),
            });
            for (const ref of boxesRef.value) {
                const el = ref.$el;
                if (OBSERVABLE_TYPES.includes(el.dataset?.type || '')) {
                    intersectionObserver.value?.observe(el);
                }
            }
        }
        function onClose() {
            boxesReaded.value = -1;
            readingClosed.value = true;
        }
        onBeforeUnmount(() => {
            intersectionObserver.value?.disconnect();
            intersectionObserver.value = undefined;
            window.removeEventListener('switchArticle', observe);
        });
        onMounted(() => {
            if (boxesRef.value.length) {
                observe();
            }
            else {
                watch(() => [boxesRef.value, props.isFolded, props.paywall], () => observe());
            }
            boxesReaded.value = props.readingIndex;
            window.addEventListener('switchArticle', observe);
        });
        return {
            BANKRUPTCY_DISCLAIMER,
            ARTICLE_BOXES,
            articleBody,
            boxesRef,
            boxesReaded,
            css,
            lastTextIndex,
            onClose,
            shouldRenderAdText,
            readingClosed,
            isStopReadingVisible,
        };
    },
});
